import { useState } from "react";
import { BookOpen, Bot, Lock, ExternalLink, LifeBuoy, ArrowRight, ListChecks } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TOPIC_ROUTES, canVisit } from "./eliContext";
import { useAuth } from "@/context/AuthContext";
import { helperInitial } from "@/lib/helper";

const IMG = (p) => `${process.env.REACT_APP_BACKEND_URL}/api/help/image/${p}`;
const summary = (t) => (t.steps || []).slice(0, 3).map((s) => s.text.replace(/\.$/, "")).join(", then ") + ".";

export function EliUser({ text }) {
  return <div className="flex justify-end" data-testid="eli-msg-user"><div className="max-w-[85%] rounded-2xl rounded-br-md bg-emerald-600 text-white text-sm px-3.5 py-2">{text}</div></div>;
}

export function EliBubble({ children, testid = "eli-msg-eli" }) {
  const { business } = useAuth();
  return <div className="flex gap-2" data-testid={testid}><div className="w-7 h-7 rounded-full bg-emerald-500/15 text-emerald-600 flex items-center justify-center shrink-0 text-xs font-bold">{helperInitial(business)}</div>
    <div className="max-w-[88%] rounded-2xl rounded-bl-md bg-secondary text-sm px-3.5 py-2.5 space-y-2 min-w-0">{children}</div></div>;
}

export function EliAnswer({ data, role, onNav, onEscalate }) {
  const [pick, setPick] = useState(null);
  const [showSteps, setShowSteps] = useState(false);
  if (data.source !== "tutorial") {
    return (
      <EliBubble testid="eli-msg-ai">
        <p className="text-xs font-semibold text-sky-600 flex items-center gap-1"><Bot className="w-3.5 h-3.5" /> Assistant answer</p>
        <p className="whitespace-pre-wrap" data-testid="eli-ai-answer">{data.answer}</p>
        <Actions onEscalate={() => onEscalate(data.escalate)} />
      </EliBubble>
    );
  }
  const topic = pick || data.topic;
  const route = TOPIC_ROUTES[topic.slug];
  if (data.matches?.length > 1 && !pick) {
    return (
      <EliBubble testid="eli-msg-options">
        <p>I found a few guides that could fit. Which one is closest?</p>
        <div className="space-y-1.5">
          {data.matches.map((m, i) => (
            <button key={i} onClick={() => setPick(m.topic)} data-testid={`eli-match-${i}`}
              className="w-full text-left rounded-lg border border-border bg-card hover:border-emerald-500/50 px-3 py-2 transition-colors">
              <span className="font-medium text-sm">{m.topic.title}</span>{i === 0 && <span className="ml-2 text-[10px] text-emerald-600 font-semibold">Best match</span>}
            </button>
          ))}
        </div>
      </EliBubble>
    );
  }
  return (
    <EliBubble testid="eli-msg-tutorial">
      <p className="text-xs font-semibold text-emerald-600 flex items-center gap-1"><BookOpen className="w-3.5 h-3.5" /> {topic.title}</p>
      {topic.role_locked ? (
        <p className="flex items-start gap-1.5 text-amber-700 dark:text-amber-400" data-testid="eli-role-locked"><Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          {topic.role_note || "You'll need an Owner or Manager for that action."}</p>
      ) : (
        <>
          <p data-testid="eli-summary">Here's the quickest way to do that: {summary(topic)}</p>
          {showSteps && (
            <ol className="list-decimal ml-4 space-y-2 text-[13px]" data-testid="eli-steps">
              {(topic.steps || []).map((s, i) => <li key={i}>{s.text}{s.image_path && <img src={IMG(s.image_path)} alt={s.caption || ""} className="mt-1 rounded-lg border border-border max-h-40 object-contain" />}</li>)}
            </ol>
          )}
        </>
      )}
      <div className="flex flex-wrap gap-1.5 pt-1">
        {!topic.role_locked && !showSteps && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setShowSteps(true)} data-testid="eli-show-how"><ListChecks className="w-3.5 h-3.5 mr-1" /> Show me how</Button>}
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onNav(`/help?topic=${topic.slug}`)} data-testid="eli-open-tutorial"><ExternalLink className="w-3.5 h-3.5 mr-1" /> Open full tutorial</Button>
        {route && !topic.role_locked && canVisit(role, route) && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onNav(route)} data-testid="eli-go-page"><ArrowRight className="w-3.5 h-3.5 mr-1" /> Go to page</Button>}
      </div>
      <Actions onEscalate={() => onEscalate(data.escalate)} />
    </EliBubble>
  );
}

function Actions({ onEscalate }) {
  return (
    <div className="flex items-center gap-2 border-t border-border/60 pt-2 text-xs text-muted-foreground">
      Still need help?
      <button onClick={onEscalate} className="inline-flex items-center gap-1 text-emerald-600 font-semibold hover:underline" data-testid="eli-contact-support"><LifeBuoy className="w-3.5 h-3.5" /> Contact Support</button>
    </div>
  );
}
